import styled from '@emotion/styled';
import { Rating } from '@mui/material';
import { formatLargeNumber } from '@shared/hooks/helpers/FormatLargeNumbers';
import { useTranslation } from 'react-i18next';
import SrOnlyText from '../text/SrOnlyText';
import { TitleXSmall } from '../text/Typography';
import TinyTag from '../tiny/TinyTag';

/**
 * Ratings below this many votes are withheld, so a single early rating can't stand in for consensus.
 * Mirrors MIN_PUBLIC_RATINGS in ExtensionMarketplaceStats; override per-surface with `minRatings`.
 */
const DEFAULT_MIN_RATINGS = 5;

/** The aggregate figures behind a rateable thing — a skill, connector, tool, or Cephable feature. */
export interface IRatingStats {
    /** Average public star rating, 0–5. */
    averageRating?: number;
    /** How many ratings the average is drawn from. */
    ratingCount?: number;
    /** Distinct users who have installed/enabled it. */
    installCount?: number;
    /** Number of comments left on it. */
    commentCount?: number;
}

export interface IRatingSummaryProps extends IRatingStats {
    /**
     * Slims the row down to figures only — icons and counts without the "installs"/"comments" words,
     * which get verbose on a card. The words are kept for screen readers and as hover tooltips.
     */
    compact?: boolean;
    /**
     * Render empty stars instead of nothing when there's no average to show yet, so every card in a
     * grid has the same shape. The numeric score is still withheld — there isn't one.
     */
    showEmptyRating?: boolean;
    /** Ratings needed before the average is shown at all. Defaults to 5. */
    minRatings?: number;
    /** When provided, the whole row becomes a button — use it to open `RateReviewDialog`. */
    onClick?: () => void;
    /** Overrides the screen-reader hint describing what activating the row does. */
    actionLabel?: string;
    className?: string;
}

/**
 * The read-only face of a rateable thing: its average score and stars, plus install and comment
 * counts as `TinyTag`s. This is the resting state everywhere ratings appear — cards, list rows, and
 * detail headers.
 *
 * Pass `onClick` to make the whole row a button that opens `RateReviewDialog`; `RatingReview` wires
 * that pairing up for you. Counts are locale-formatted and compacted (1200 → "1.2k"), and the
 * average is withheld until `minRatings` people have voted.
 */
export default function RatingSummary({
    averageRating,
    ratingCount,
    installCount,
    commentCount,
    compact = false,
    showEmptyRating = false,
    minRatings = DEFAULT_MIN_RATINGS,
    onClick,
    actionLabel,
    className,
}: Readonly<IRatingSummaryProps>) {
    const { t, i18n } = useTranslation('common');
    const locale = i18n.language;

    const hasEnoughRatings = (ratingCount ?? 0) >= minRatings;
    const showAverage = hasEnoughRatings && averageRating !== undefined;

    const content = (
        <>
            {(showAverage || showEmptyRating) && (
                <AverageGroup>
                    {showAverage && (
                        <Average>
                            {averageRating.toLocaleString(locale, {
                                minimumFractionDigits: 1,
                                maximumFractionDigits: 1,
                            })}
                        </Average>
                    )}
                    <AverageStars value={showAverage ? averageRating : 0} precision={0.5} size="small" readOnly />
                    <SrOnlyText>
                        {showAverage
                            ? t('rating.summary.ratingCount', {
                                  count: ratingCount ?? 0,
                              })
                            : t('rating.summary.noRatings')}
                    </SrOnlyText>
                </AverageGroup>
            )}
            {installCount !== undefined && (
                <TinyTag
                    icon="download"
                    value={formatLargeNumber(installCount, locale, t)}
                    title={t('rating.summary.installs')}
                    hideLabel={compact}
                />
            )}
            {commentCount !== undefined && (
                <TinyTag
                    icon="chat"
                    value={formatLargeNumber(commentCount, locale, t)}
                    title={t('rating.summary.comments')}
                    hideLabel={compact}
                />
            )}
        </>
    );

    if (!onClick) {
        return (
            <Row $compact={compact} className={className}>
                {content}
            </Row>
        );
    }

    return (
        <RowButton
            type="button"
            $compact={compact}
            className={className}
            // These rows sit inside clickable cards; a rating click must not also open the card.
            onClick={(event) => {
                event.stopPropagation();
                onClick();
            }}>
            {content}
            <SrOnlyText>{actionLabel ?? t('rating.summary.openReview')}</SrOnlyText>
        </RowButton>
    );
}

/**
 * The padding lives here rather than on the button form, so a read-only row and a rateable one occupy
 * exactly the same box — otherwise the ratings strip shifts by a couple of pixels between a surface the
 * user can rate on and one they can't (browse vs. installed, for the same item).
 */
const Row = styled.div<{ $compact?: boolean }>`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    padding: 0.125rem 0;
    /* Label-less tags read as separate figures at a tighter gap; spelled-out ones need the air. */
    gap: ${(props) => (props.$compact ? '0.625rem' : '0.75rem')};
`;

const RowButton = styled(Row.withComponent('button'))`
    border: none;
    background: none;
    text-align: left;
    cursor: pointer;
    /* <button> doesn't inherit the themed text color, and the stars/icons use currentColor. */
    color: ${(props) => props.theme.color.text};

    &:hover {
        opacity: 0.8;
    }
    &:focus-visible {
        outline: 2px solid ${(props) => props.theme.color.accent};
        outline-offset: 2px;
        border-radius: 0.25rem;
    }
`;

const AverageGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 0.375rem;
`;

const Average = styled(TitleXSmall)`
    color: ${(props) => props.theme.color.text};
`;

/**
 * The aggregate stars read as text, not as MUI's amber: filled stars take the themed text color and
 * the remainder the muted body color. The rating *input* in `RateReviewDialog` keeps the amber, so
 * the thing you can act on stays visually distinct from the read-only score.
 */
const AverageStars = styled(Rating)`
    & .MuiRating-iconFilled {
        color: ${(props) => props.theme.color.text};
    }
    & .MuiRating-iconEmpty {
        color: ${(props) => props.theme.color.textBody};
    }
`;
